import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building2, Zap, Sun, ArrowRight, Trash2 } from 'lucide-react';
import { EPSESite } from '@/types/epse';
import { useNavigate } from 'react-router-dom';

interface EPSESiteCardProps {
  site: EPSESite;
  onDelete?: (id: string) => void;
}

export function EPSESiteCard({ site, onDelete }: EPSESiteCardProps) {
  const navigate = useNavigate();
  
  return (
    <Card className="group hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">{site.name}</CardTitle>
              {site.organization && (
                <p className="text-sm text-muted-foreground">
                  {site.organization.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {site.tags.slice(0, 2).map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {site.tags.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{site.tags.length - 2}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-500" />
            <div>
              <p className="text-muted-foreground text-xs">Contractlimiet</p>
              <p className="font-medium">{site.connection_kw_limit} kW</p>
            </div>
          </div>
          
          {site.pv_installed_kwp && (
            <div className="flex items-center gap-2">
              <Sun className="h-4 w-4 text-yellow-500" />
              <div>
                <p className="text-muted-foreground text-xs">PV Vermogen</p>
                <p className="font-medium">{site.pv_installed_kwp} kWp</p>
              </div>
            </div>
          )}
          
          {site.annual_kwh_total && (
            <div className="col-span-2">
              <p className="text-muted-foreground text-xs">Jaarverbruik</p>
              <p className="font-medium">
                {Math.round(site.annual_kwh_total).toLocaleString('nl-NL')} kWh
              </p>
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(site.id);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/crm/analyses/epse/${site.id}`)}
          >
            Bekijken
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
